/**
 * Representa uma resposta de erro
 */
export interface ErrorResponse {
    /** Código do erro recebido */
    code: number;
    /** Descrição do erro recebido */
    description: string
    /** Detalhes do erro recebido */
    details: Array<string>;
}

/**
 * Representa a resposta de erro com suas informações tratadas para apresentação em tela
 */
export interface ErrorDescriptionDetails {
    /** Descrição do erro internacionalizada */
    description: string;
    /** Detalhes do erro internacionalizado */
    details: string;
}


/**
 * Mapeamento genérico entre enums e valores.
 *
 * Pode ser usado tanto para mapear uma enum de texto para um código de erro,
 * ou de um código de erro para um texto.
 */
export type ErrorCodeMap<T extends string | number> = {
    [key in T]: string | number;
}

/**
 * Enum para os códigos de status HTTP.
 *
 * Utilizando o padrão TitleCase ao invés de deixar tudo maiúsculo separado
 * por subtraço, porque essa é a forma que o Angular usa, facilitando assim
 * compatibilidade quando um upgrade for feito.
 *
 * Quando atualizar o Angular para uma versão superior a 12,
 * remover essa definição e utilizar a disponibilizada pelo Angular.
 *
 * @See https://angular.io/api/common/http/HttpStatusCode
 */
export enum HttpStatusCode {
    Continue                      = 100,
    SwitchingProtocols            = 101,
    Processing                    = 102,
    EarlyHints                    = 103,
    Ok                            = 200,
    Created                       = 201,
    Accepted                      = 202,
    NonAuthoritativeInformation   = 203,
    NoContent                     = 204,
    ResetContent                  = 205,
    PartialContent                = 206,
    MultiStatus                   = 207,
    AlreadyReported               = 208,
    ImUsed                        = 226,
    MultipleChoices               = 300,
    MovedPermanently              = 301,
    Found                         = 302,
    SeeOther                      = 303,
    NotModified                   = 304,
    UseProxy                      = 305,
    Unused                        = 306,
    TemporaryRedirect             = 307,
    PermanentRedirect             = 308,
    BadRequest                    = 400,
    Unauthorized                  = 401,
    PaymentRequired               = 402,
    Forbidden                     = 403,
    NotFound                      = 404,
    MethodNotAllowed              = 405,
    NotAcceptable                 = 406,
    ProxyAuthenticationRequired   = 407,
    RequestTimeout                = 408,
    Conflict                      = 409,
    Gone                          = 410,
    LengthRequired                = 411,
    PreconditionFailed            = 412,
    PayloadTooLarge               = 413,
    UriTooLong                    = 414,
    UnsupportedMediaType          = 415,
    RangeNotSatisfiable           = 416,
    ExpectationFailed             = 417,
    ImATeapot                     = 418,
    MisdirectedRequest            = 421,
    UnprocessableEntity           = 422,
    Locked                        = 423,
    FailedDependency              = 424,
    TooEarly                      = 425,
    UpgradeRequired               = 426,
    PreconditionRequired          = 428,
    TooManyRequests               = 429,
    RequestHeaderFieldsTooLarge   = 431,
    UnavailableForLegalReasons    = 451,
    InternalServerError           = 500,
    NotImplemented                = 501,
    BadGateway                    = 502,
    ServiceUnavailable            = 503,
    GatewayTimeout                = 504,
    HttpVersionNotSupported       = 505,
    VariantAlsoNegotiates         = 506,
    InsufficientStorage           = 507,
    LoopDetected                  = 508,
    NotExtended                   = 510,
    NetworkAuthenticationRequired = 511
}

/**
 * Abaixo segue o cadastro de todos os códigos de erro dos serviços do nms-services.
 * Os códigos devem ser únicos, e devem ser agrupados por serviço ou lib.
 *
 * Cada serviço possui a sua própria enum e ao final é criado uma consolidação de todos
 * os códigos de erro no objeto @see NmsError
 *
 * Deve se manter alinhada com a classe br.ind.datacom.nms.commons.error.ErrorCodes
 */

export enum NesError {
    UNKNOWN_ERROR                   =  1,
    DEVICE_ACCESS_ERROR             =  2,
    DEVICE_COMMUNICATION_ERROR      =  3,
    CONFIGURATION_CHANGED_ERROR     =  4,
    NETWORK_ELEMENT_NOT_IDENTIFIED  =  5,
    DEVICE_LOCKED                   =  6,
    DEVICE_AUTHENTICATION_ERROR     =  7,
    UNEXPECTED_DEVICE_BEHAVIOR      =  8,
    MONGO_ACCESS_ERROR              =  9,
    INVENTORY_UNAVAILABLE_ERROR     = 10,
    DISCOVERY_ERROR                 = 11,
    DEVICE_CONFIGURATION_ERROR      = 12,
    NETWORK_ELEMENT_NOT_FOUND_IN_DB = 13,
    TIMEOUT_ERROR                   = 14,
    PARSER_ERROR                    = 15,
    UNKNOW_DEVICE_ERROR             = 16,
    FIRMWARE_VERSION_ERROR          = 17,
    ACCESS_DENIED_ERROR             = 18
}

export enum DeviceAccessError {
    GENERIC_DEVICE_ERROR = 1,
    UNREACHABLE_DEVICE   = 2
}

export enum GenericError {
    UNEXPECTED_ERROR = 20
}

export enum DatabaseError {
    DATABASE_ERROR       = 101,
    DEVICE_ALREADY_ADDED = 102
}

export enum DataCollectorError {
    TOO_MANY_RESULTS_FOUND = 201
}

export enum AcsServerError {
    ACS_CONNECTION_REQUEST_ERROR                = 300,
    ACS_BAD_CONDITIONAL_COMMAND_ERROR           = 301,
    ACS_COULD_NOT_ACCESS_SERVER_ERROR           = 302,
    ACS_CONNECTION_REQUEST_AUTHENTICATION_ERROR = 303,
    ACS_CPE_MESSAGE_TIMEOUT                     = 304,
    ACS_UNEXPECTED_ERROR                        = 305,
    UNEXPECTED_CPE_MESSAGE                      = 306,
    ACS_DUPLICATED_USER                         = 307
}

export enum NmsSecurityError {
    INVALID_CAPTCHA = 350
}

export enum NotificationServiceError {
    INVALID_NOTIFICATION_PREFERENCES_ERROR = 400
}

export enum SchedulerError {
    JOB_NOT_FOUND_ERROR                              = 450,
    SCHEDULER_UNEXPECTED_ERROR                       = 451,
    INVALID_CRON_EXPRESSION_ERROR                    = 452,
    SCHEDULER_REMOVAL_ERROR                          = 453,
    JOB_EXECUTION_ERROR                              = 454,
    USER_WITHOUT_SCHEDULER_PERMISSION_ERROR          = 455,
    USER_WITHOUT_JOB_TYPE_SCHEDULER_PERMISSION_ERROR = 456,
    USER_WITHOUT_CREATE_SCHEDULER_PERMISSION_ERROR   = 457,
    INVALID_BACKUP_DEVICE_MODEL_SCHEDULER_ERROR      = 458
}

export enum NmsTemplateError {
    DEVICE_WITHOUT_MANAGE_PERMISSION_ERROR            = 500,
    AUTO_SELECTION_VARIABLE_VALUE_NOT_AVAILABLE_ERROR = 501,
    AUDIT_SETTINGS_VALIDATION_ERROR                   = 502
}

export enum DcbError {
    UNKNOWN_DCB_ERROR                 = 550,
    UNSUPPORTED_DCB_FILES_ERROR       = 551,
    DEVICE_COMMUNICATION_DCB_ERROR    = 552,
    CONFIG_CHANGED_DCB_ERROR          = 553,
    DEVICE_NOT_IDENTIFIED_DCB_ERROR   = 554,
    DEVICE_LOCKED_DCB_ERROR           = 555,
    DEVICE_AUTHENTICATION_DCB_ERROR   = 556,
    DATABASE_ACCESS_DCB_ERROR         = 557,
    SERVICE_UNAVAILABLE_DCB_ERROR     = 558,
    DEVICE_CONFIGURATION_DCB_ERROR    = 559,
    FIRST_POLLING_FAILED_DCB_ERROR    = 560,
    FIRMWARE_VERSION_DCB_ERROR        = 562,
    ACCESS_DENIED_DCB_ERROR           = 563,
    DEVICE_CONFIG_PARSER_DCB_ERROR    = 564,
    CORRUPTED_FILE_DCB_ERROR          = 565,
    PROTECTED_DCB_ERROR               = 566,
    DCB_NOT_FOUND                     = 567,
    EXPORT_CONFIG_PARSER_DCB_ERROR    = 568,
    POLLER_COMMUNICATION_DCB_ERROR    = 569,
    DCB_RETENTION_CONFIGURATION_ERROR = 570,
    DCB_MODEL_OPERATION_ERROR         = 571,
}

export enum ApiNorthboundError {
    BAD_REQUEST_ERROR     = 600,
    DATA_NOT_FOUND_ERROR  = 601,
    DUPLICATED_DATA_ERROR = 602,
    VALIDATION_ERROR      = 603,
    FORBIDDEN_ERROR       = 604,
    INTERNAL_SERVER_ERROR = 605,
    VARIABLE_VALUES_ERROR = 606
}

export enum LinkError {
    DEVICE_NOT_FOUND_LINK_ERROR                  = 650,
    EMPTY_LINK_NAME_ERROR                        = 651,
    DUPLICATED_NAME_LINK_ERROR                   = 652,
    PART_OF_RESOURCE_LINK_ERROR                  = 653,
    PORTS_VALIDATION_LINK_ERROR                  = 654,
    SLOTS_VALIDATION_LINK_ERROR                  = 655,
    LINK_TYPE_ERROR                              = 656,
    PORT_ALREADY_LINKABLE_ERROR                  = 657,
    PORT_NOT_COMPATIBLE_ERROR                    = 658,
    UNSUPPORTED_LINK_BETWEEN_DEVICE_MODELS_ERROR = 659,
    AT_LEAST_ONE_MEMBER_PORT_ERROR               = 660,
    DIFFERENT_NUMBER_OF_PORTS_ERROR              = 661,
    PORT_PART_OF_PORTCHANNEL_ERROR               = 662,
    MEMBER_PORT_ALREADY_LINKED_ERROR             = 663,
    PRIVATE_VLAN_PORT_ERROR                      = 664,
    DUPLICATED_DEVICES_ERROR                     = 665,
    LINK_WITHOUT_CHANGES_ERROR                   = 666,
    LINK_NAME_MAX_SIZE_ERROR                     = 667,
    COST_RANGE_VALIDATOR_ERROR                   = 668,
    NMS_MAP_NOT_REFLECT_LINK_CHANGE_ERROR        = 669,
    POLLER_COMMUNICATION_LINK_ERROR              = 670,
    DISTANCE_VALIDATOR_ERROR                     = 671,
    ATTENUATION_VALIDATOR_ERROR                  = 672,
    UNSELECT_MUX_DEMUX_ERROR                     = 673,
    SAME_TRANSMISSION_ERROR                      = 674,
    SOME_TRANSMISSION_NONE_ERROR                 = 675,
    MUX_NOT_COMPATIBLE_ERROR                     = 676,
    DWDM_LINK_INVALID_DEVICES_ERROR              = 677
}

export enum MuxError {
    MUX_NAME_REQUIRED_ERROR                = 700,
    MUX_NAME_DUPLICATED_ERROR              = 701,
    MUX_NAME_LENGTH_ERROR                  = 702,
    MUX_SERIAL_NUMBER_FORMAT_ERROR         = 703,
    MUX_SERIAL_NUMBER_LENGTH_ERROR         = 704,
    MUX_MODEL_REQUIRED_ERROR               = 705,
    MUX_MODEL_INVALID_ERROR                = 706,
    MUX_ASSOCIATED_DEVICES_SIZE_ERROR      = 707,
    MUX_ASSOCIATED_DEVICES_MODEL_ERROR     = 708,
    MUX_DESCRIPTION_LENGTH_ERROR           = 709,
    USED_MUX_MODEL_MODIFIED_ERROR          = 710,
    MUX_PART_OF_LINK_ERROR                 = 711,
    MUX_NOT_FOUND_ERROR                    = 712,
    MUX_DISASSOCIATED_DEVICES_MODEL_ERROR  = 713,
    MUX_PART_OF_CIRCUIT_ERROR              = 714
}

export enum DwdmCircuitsError {
    DWDM_CIRCUIT_INVALID_DEVICES_ERROR     = 800,
    DWDM_CIRCUIT_SAME_ENDS_ERROR           = 801,
    DWDM_CIRCUIT_PORT_COUNT_MATCHES_ERROR  = 802,
    DWDM_CIRCUIT_DEVICE_STATUS_ERROR       = 803,
    DWDM_CIRCUIT_PORT_MODEL_ERROR          = 804,
    DWDM_CIRCUIT_PORT_IN_USE_ERROR         = 805,
    DWDM_CIRCUIT_MUX_REQUIRED_ERROR        = 806,
    DWDM_CIRCUIT_BOTH_END_MUX_ERROR        = 807,
    DWDM_CIRCUIT_MUX_ASSOCIATION_ERROR     = 808,
    DWDM_CIRCUIT_DEVICE_PERMISSION_ERROR   = 809,
    DWDM_CIRCUIT_REMOVE_ACTIVATION_ERROR   = 810,
    DWDM_CIRCUIT_NOT_FOUND                 = 811,
    DWDM_CIRCUIT_REMOVED_BUT_STILL_IN_DB   = 812,
    DWDM_CIRCUIT_INVALID_TX_POWER          = 813,
    DWDM_CIRCUIT_CHANNEL_MISMATCH_ERROR    = 814,
    DWDM_CIRCUIT_CHANNEL_MULTIRATE_MATCH_ERROR = 815,
    DWDM_CIRCUIT_CHANNEL_FREQUENCY_ERROR   = 816,
    DWDM_CIRCUIT_CHANNEL_USED_BY_MUX_ERROR   = 817,
    DWDM_CIRCUIT_CHANNEL_IN_USE_BY_PORT_ERROR = 818,
    DWDM_CIRCUIT_INVALID_MUX_CHANNEL_RANGE = 819,
    DWDM_CIRCUIT_UPDATED_BUT_NOT_IN_DB = 820,
    DWDM_CIRCUIT_INTERMEDIATE_WITHOUT_EDFA_PORT = 821,
    DWDM_CIRCUIT_INTERMEDIATE_IS_EQUALS_ONE_END = 822,
    DWDM_CIRCUIT_LINK_NOT_ACTIVE_SITUATION = 823,
    DWDM_CIRCUIT_LINK_TYPE_IS_NOT_DWDM = 824,
    DWDM_CIRCUIT_LINK_MUX_CANNOT_BE_USED = 825,
    DWDM_CIRCUIT_LINK_DEVICES_NOT_IN_CIRCUIT = 826,
    DWDM_CIRCUIT_LINK_TX_RX_REQUIRED = 827,
    DWDM_CIRCUIT_LINK_INVALID_COUNT_ON_INTERMEDIATE = 828,
    DWDM_CIRCUIT_LINK_TX_RX_MISMATCH = 829,
    DWDM_CIRCUIT_AMPLIFIER_MUST_BE_CONFIGURED = 830,
    DWDM_CIRCUIT_AMPLIFIER_INVALID_CONFIGURED_VALUE = 831,
    DWDM_CIRCUIT_ENDS_MUST_HAVE_ONLY_ONE_OSC = 832,
    DWDM_CIRCUIT_ENDS_TX_MUST_HAVE_A_TX_OSC = 833,
    DWDM_CIRCUIT_ENDS_RX_MUST_HAVE_A_RX_OSC = 834,
    DWDM_CIRCUIT_MULTIPLE_OSC_MANAGEMENT_PROFILES = 835,
    DWDM_CIRCUIT_NAME_INVALID_LENGTH = 836,
    DWDM_CIRCUIT_DUPLICATED_NAME = 837,
    DWDM_CIRCUIT_DESCRIPTION_INVALID_LENGTH = 838,
    DWDM_CIRCUIT_LINK_FOUR_HUNDRED_CANNOT_BE_USED = 839,
    DWDM_CIRCUIT_LINKS_REQUIRED = 840,
    DWDM_CIRCUIT_INTERMEDIATES_MUST_HAVE_MAX_TWO_OSC = 841,
    DWDM_CIRCUIT_LINKS_WITH_MUX_NOT_ALLOWED = 842,
    DWDM_CIRCUIT_INTERMEDIATES_MUST_HAVE_EDFA_PORT = 843,
    DWDM_CIRCUIT_CREATE_UPDATE_ACTIVATION_ERROR = 844,
    DWDM_CIRCUIT_EDFA_PORT_ALREADY_IN_USE = 845,
    DWDM_CIRCUIT_CARD_NOT_PROVISIONED = 846
}

/**
 * Cadastro de todos os códigos de erro dos serviços do nms-services.
 *
 * Erros consolidados fazendo uma união entre todas as enums individuais.
 *
 * Os códigos devem ser únicos, e devem ser agrupados por serviço ou lib.
 * Deve se manter alinhada com a classe br.ind.datacom.nms.commons.error.ErrorCodes
 *
 * Aqui são definidos os valores, de modo que seja possível usar,
 * por exemplo, NmsError.UNKNOWN_ERROR em um case de um switch.
 */
export const NmsError = {
    ...DeviceAccessError,
    ...DatabaseError,
    ...DataCollectorError,
    ...AcsServerError,
    ...NmsSecurityError,
    ...NotificationServiceError,
    ...SchedulerError,
    ...NmsTemplateError,
    ...DcbError,
    ...ApiNorthboundError,
    ...LinkError,
    ...MuxError,
    ...DwdmCircuitsError,
    ...GenericError
};

/**
 * Definição de NmsError como tipo.
 * Deve ter o mesmo nome da constante NmsError acima.
 *
 * Aqui é definido o tipo, de modo que seja possível usar,
 * por exemplo, NmsError como tipo de variável/retorno.
 *
 * (e.g. function example(error: NmsError) {}, function example(): NmsError {})
 */
export type NmsError = keyof typeof NmsError;
